import React, { useState, useEffect, useRef } from 'react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { GenericView, SearchInput } from '../../components/common/table';
import { useAuth } from '../../contexts/AuthProvider';

// Note: /api/crm/proposal-stages is unpaginated on the backend (returns a plain
// list, not a Page), so pagination/search below are applied client-side.
const ProposalStage = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canProposalStage = (key) => can(key, 'proposalStage');

  // Pagination (applied client-side over the full list returned by the API)
  const pagination = usePagination(10);

  // Search
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Data and form state
  const [allProposalStages, setAllProposalStages] = useState([]);
  const [proposalStages, setProposalStages] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  // Fetch full list (backend has no pagination for this endpoint)
  const fetchProposalStages = async (searchValue = '') => {
    try {
      const response = searchValue
        ? await api.get('/crm/proposal-stages/search', { params: { q: searchValue } })
        : await api.get('/crm/proposal-stages');
      setAllProposalStages(response.data || []);
    } catch (error) {
      setAllProposalStages([]);
    }
  };

  useEffect(() => {
    fetchProposalStages(appliedSearchTerm);
  }, [appliedSearchTerm]);

  // Client-side pagination over the fetched list
  useEffect(() => {
    const total = allProposalStages.length;
    const totalPages = Math.max(1, Math.ceil(total / pagination.size));
    const start = pagination.page * pagination.size;
    const pageItems = allProposalStages.slice(start, start + pagination.size);
    setProposalStages(pageItems);
    pagination.setTotal(total);
    pagination.setTotalPages(totalPages);
    pagination.setCurrentPageItems(pageItems.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProposalStages, pagination.page, pagination.size]);

  const handleCreate = () => {
    if (!canProposalStage('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setFormData({ name: '', description: '' });
    setEditingId(null);
    setErrors({});
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById('modal-proposal-stage'));
      modal.show();
    }
  };

  // Open modal for edit
  const handleEdit = (item) => {
    if (!canProposalStage('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setFormData({ name: item.name, description: item.description || '' });
    setEditingId(item.id);
    setErrors({});
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = new window.bootstrap.Modal(document.getElementById('modal-proposal-stage'));
      modal.show();
    }
  };

  // Real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'name') {
      error = validateField('name', value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateField('name', formData.name, { required: true });
    if (error) {
      setErrors({ name: error });
      return;
    }
    try {
      if (editingId) {
        await api.put(`/crm/proposal-stages/${editingId}`, { name: formData.name, description: formData.description });
        setSuccessMessage('Proposal stage has been successfully updated.');
      } else {
        await api.post('/crm/proposal-stages', { name: formData.name, description: formData.description });
        setSuccessMessage('Proposal stage has been successfully added.');
      }
      setEditingId(null);
      setFormData({ name: '', description: '' });
      setErrors({});
      await fetchProposalStages(appliedSearchTerm);
      const modal = document.getElementById('modal-proposal-stage');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save proposal stage.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid proposal stage data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 404:
            errorMessage = 'Proposal stage not found.';
            technicalDetails = 'The item may have already been deleted';
            break;
          case 409:
            errorMessage = 'A proposal stage with this name already exists.';
            technicalDetails = `Duplicate entry for name: "${formData.name}"`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the proposal stage.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Delete selected (backend has no bulk-delete endpoint, so delete one by one)
  const deleteSelected = () => {
    if (!canProposalStage('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one proposal stage to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        await Promise.all(selectedIds.map((id) => api.delete(`/crm/proposal-stages/${id}`)));
        setSelectedIds([]);
        await fetchProposalStages(appliedSearchTerm);
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected proposal stages have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete proposal stages.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more proposal stages not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  // Modal cleanup
  useEffect(() => {
    const modal = document.getElementById('modal-proposal-stage');
    if (!modal) return;
    const handleModalHidden = () => {
      setFormData({ name: '', description: '' });
      setEditingId(null);
      setErrors({});
    };
    modal.addEventListener('hidden.bs.modal', handleModalHidden);
    return () => {
      modal.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  // Edit selected (only works for single selection)
  const editSelected = () => {
    if (selectedIds.length !== 1) {
      setSuccessMessage('Please select exactly one proposal stage to edit');
      setShowSuccessModal(true);
      return;
    }
    const selectedItem = proposalStages.find(item => item.id === selectedIds[0]);
    if (selectedItem) {
      handleEdit(selectedItem);
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'description', label: 'Description', sortable: false, render: (value) => value || '-' },
    { key: 'createdAt', label: 'Created Date', sortable: true, render: (value) => value ? new Date(value).toLocaleDateString() : '-' },
  ];

  return (
    <>
        {/* Page header */}
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title d-flex align-items-center">
                  Proposal Stage
                </h2>
              </div>
              <div className="col-auto ms-auto d-print-none">
                <div className="btn-list">
                  <a href="#" className="btn btn-primary btn-5 d-none d-sm-inline-block"
                    onClick={handleCreate}>
                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                    Create
                  </a>
                  <a href="#" className="btn btn-primary btn-6 d-sm-none btn-icon"
                    onClick={handleCreate} aria-label="Create new report">
                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-2">
                      <path d="M12 5l0 14" />
                      <path d="M5 12l14 0" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="page-body">
          <div className="container-xl">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <GenericView
                    data={proposalStages}
                    columns={columns}
                    isLoading={capabilitiesLoading}
                    emptyStateMessage="No proposal stages found"
                    checkboxSelection={true}
                    selectedRows={selectedIds}
                    onSelectRow={(itemId, isSelected) => {
                      if (isSelected) {
                        setSelectedIds([...selectedIds, itemId]);
                      } else {
                        setSelectedIds(selectedIds.filter(id => id !== itemId));
                      }
                    }}
                    onSelectAllRows={(itemIds) => {
                      if (Array.isArray(itemIds) && itemIds.length > 0) {
                        setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                    onEdit={canProposalStage('edit') ? (item) => {
                      setSelectedIds([item.id]);
                      handleEdit(item);
                    } : undefined}
                    onDelete={canProposalStage('delete') ? (item) => {
                      setSelectedIds([item.id]);
                      deleteSelected();
                    } : undefined}
                    searchComponent={
                      <div className="d-flex gap-2">
                        <SearchInput
                          value={searchTerm}
                          onChange={handleSearchInputChange}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSearchSubmit();
                            }
                          }}
                          placeholder="Search..."
                          uniqueId="proposal-stage-search"
                        />
                        <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                          Search
                        </button>
                      </div>
                    }
                    actionButtons={
                      <div className="d-flex gap-2">
                        {(canProposalStage('edit') || canProposalStage('delete')) && (
                          <div ref={actionsContainerRef} className="dropdown">
                            <a
                              ref={actionsToggleRef}
                              href="#"
                              className="btn dropdown-toggle"
                              data-bs-toggle="dropdown"
                              aria-expanded={isActionsOpen}
                              onClick={(e) => {
                                e.preventDefault();
                                const inst = actionsDropdownInstanceRef.current;
                                if (inst?.toggle) inst.toggle();
                                else setIsActionsOpen(prev => !prev);
                              }}
                            >Actions</a>
                            <div className="dropdown-menu">
                              {canProposalStage('edit') && (
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                                  style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                                  Edit
                                </a>
                              )}
                              {canProposalStage('delete') && (
                                <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                  style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                  Delete
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    }
                    pagination={{
                      currentPage: pagination.page + 1,
                      totalRows: pagination.total,
                      rowsPerPage: pagination.size,
                      onPageChange: (page) => pagination.setPage(page - 1),
                      onRowsPerPageChange: (size) => {
                        pagination.setSize(size);
                        pagination.setPage(0);
                      }
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    uniqueId="proposal-stage-table"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Unified Add/Edit Modal */}
      <div className="modal modal-blur fade" id="modal-proposal-stage" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingId ? 'Edit Proposal Stage' : 'Create Proposal Stage'}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="proposalStageName" className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="proposalStageName"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter proposal stage name"
                    maxLength={250}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <div className="mb-3">
                  <label htmlFor="proposalStageDescription" className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    id="proposalStageDescription"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description (optional)"
                    maxLength={500}
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message={selectedIds.length > 0
          ? "Are you sure you want to delete the selected Proposal stages? This action cannot be undone."
          : "Are you sure you want to delete this Proposal stage? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />
      {/* Add FailModal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default ProposalStage;
